import { forwardRef, useState, type ReactNode } from 'react';

import {
  useMonkeysBaseAppearance,
  useMonkeysComponentAttributes,
  useMonkeysLocaleMessages,
} from '../../provider/context';
import type { BasePaginationProps } from './types';
import { resolveBaseAppearance } from './theme';
import { cn, hasRenderableNode } from './utils';

type PaginationItem = number | 'ellipsis';

const paginationModes = new Set(['compact', 'pages']);

function clampPage(page: number, pageCount: number) {
  return Math.min(Math.max(page, 1), Math.max(pageCount, 1));
}

function assertPositiveInteger(value: unknown, name: string) {
  if (!Number.isSafeInteger(value) || (value as number) < 1) {
    throw new RangeError(
      `BasePagination ${name} must be a positive safe integer.`,
    );
  }
}

function assertNonNegativeInteger(value: unknown, name: string) {
  if (!Number.isSafeInteger(value) || (value as number) < 0) {
    throw new RangeError(
      `BasePagination ${name} must be a non-negative safe integer.`,
    );
  }
}

function assertOptionalControlLabel(value: ReactNode, name: string) {
  if (typeof value === 'string' && !value.trim()) {
    throw new TypeError(
      `BasePagination ${name} must be a non-empty string when provided.`,
    );
  }
}

function assertAccessibleName(
  ariaLabel: unknown,
  ariaLabelledBy: unknown,
) {
  if (
    ariaLabel !== undefined &&
    (typeof ariaLabel !== 'string' || !ariaLabel.trim())
  ) {
    throw new TypeError(
      'BasePagination aria-label must be a non-empty string when provided.',
    );
  }
  if (
    ariaLabelledBy !== undefined &&
    (typeof ariaLabelledBy !== 'string' || !ariaLabelledBy.trim())
  ) {
    throw new TypeError(
      'BasePagination aria-labelledby must contain at least one valid ID token.',
    );
  }
  if (typeof ariaLabelledBy === 'string') {
    const tokens = ariaLabelledBy.trim().split(/\s+/);
    if (tokens.some((token) => /[\u0000-\u001f\u007f]/u.test(token))) {
      throw new TypeError(
        'BasePagination aria-labelledby must contain only valid ID tokens.',
      );
    }
  }
}

function assertLocaleString(value: unknown, name: string) {
  if (typeof value !== 'string' || !value.trim()) {
    throw new TypeError(
      `BasePagination locale ${name} must resolve to a non-empty string.`,
    );
  }
  return value;
}

function getControlContent(value: ReactNode, fallback: string) {
  return hasRenderableNode(value) ? value : fallback;
}

function getControlAriaLabel(value: ReactNode, fallback: string) {
  return typeof value === 'string' && value.trim() ? value : fallback;
}

function getPageItems(
  currentPage: number,
  pageCount: number,
  boundaryCount: number,
  siblingCount: number,
): PaginationItem[] {
  const pages = new Set<number>();
  const addPage = (page: number) => {
    if (page >= 1 && page <= pageCount) pages.add(page);
  };

  for (let offset = 0; offset < boundaryCount; offset += 1) {
    addPage(1 + offset);
    addPage(pageCount - offset);
  }
  for (let offset = -siblingCount; offset <= siblingCount; offset += 1) {
    addPage(currentPage + offset);
  }

  const sortedPages = [...pages].sort((left, right) => left - right);
  const items: PaginationItem[] = [];

  sortedPages.forEach((page, index) => {
    const previousPage = sortedPages[index - 1];
    if (previousPage !== undefined) {
      const gap = page - previousPage;
      if (gap === 2) items.push(previousPage + 1);
      if (gap > 2) items.push('ellipsis');
    }
    items.push(page);
  });

  return items;
}

export const BasePagination = forwardRef<HTMLDivElement, BasePaginationProps>(
  function BasePagination(
    {
      page,
      defaultPage,
      pageSize = 20,
      total = 0,
      pageCount: pageCountProp,
      hasNext,
      hasPrevious,
      mode = 'compact',
      siblingCount = 1,
      boundaryCount = 1,
      label,
      firstLabel,
      previousLabel,
      nextLabel,
      lastLabel,
      appearance,
      className,
      classNames,
      style,
      onPageChange,
      role = 'navigation',
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      ...paginationProps
    },
    ref,
  ) {
    const messages = useMonkeysLocaleMessages().pagination;
    const resolvedAppearance = useMonkeysBaseAppearance(appearance);
    const componentAttributes = useMonkeysComponentAttributes();
    const theme = resolveBaseAppearance(resolvedAppearance);
    const [uncontrolledPage, setUncontrolledPage] = useState(
      () => defaultPage ?? 1,
    );

    if (page !== undefined) assertPositiveInteger(page, 'page');
    if (defaultPage !== undefined) {
      assertPositiveInteger(defaultPage, 'defaultPage');
    }
    assertPositiveInteger(pageSize, 'pageSize');
    assertNonNegativeInteger(total, 'total');
    if (pageCountProp !== undefined) assertPositiveInteger(pageCountProp, 'pageCount');
    assertNonNegativeInteger(siblingCount, 'siblingCount');
    assertNonNegativeInteger(boundaryCount, 'boundaryCount');
    if (!paginationModes.has(mode)) {
      throw new TypeError('BasePagination mode must be "compact" or "pages".');
    }
    if (onPageChange !== undefined && typeof onPageChange !== 'function') {
      throw new TypeError('BasePagination onPageChange must be a function.');
    }
    assertOptionalControlLabel(firstLabel, 'firstLabel');
    assertOptionalControlLabel(previousLabel, 'previousLabel');
    assertOptionalControlLabel(nextLabel, 'nextLabel');
    assertOptionalControlLabel(lastLabel, 'lastLabel');
    assertAccessibleName(ariaLabel, ariaLabelledBy);

    const navigationLabel = assertLocaleString(
      messages.navigation,
      'navigation',
    );
    const firstMessage = assertLocaleString(messages.first, 'first');
    const previousMessage = assertLocaleString(messages.previous, 'previous');
    const nextMessage = assertLocaleString(messages.next, 'next');
    const lastMessage = assertLocaleString(messages.last, 'last');
    const pageCount = pageCountProp ?? Math.max(Math.ceil(total / pageSize), 1);
    const isControlled = page !== undefined;
    const isUncontrolled = !isControlled && defaultPage !== undefined;
    const currentPage = clampPage(
      isControlled ? page : isUncontrolled ? uncontrolledPage : 1,
      pageCount,
    );
    const start = total > 0 ? (currentPage - 1) * pageSize + 1 : 0;
    const end = Math.min(currentPage * pageSize, total);
    const canPrevious = hasPrevious ?? currentPage > 1;
    const canNext = hasNext ?? currentPage < pageCount;
    const pageItems =
      mode === 'pages'
        ? getPageItems(currentPage, pageCount, boundaryCount, siblingCount)
        : [];

    const requestPage = (requestedPage: number) => {
      const nextPage = clampPage(requestedPage, pageCount);
      if (nextPage === currentPage) return;
      if (isUncontrolled) setUncontrolledPage(nextPage);
      onPageChange?.(nextPage);
    };

    const renderControl = (
      slot: 'first' | 'previous' | 'next' | 'last',
      targetPage: number,
      enabled: boolean,
      content: ReactNode,
      accessibleLabel: string,
    ) => (
      <button
        type="button"
        disabled={!enabled}
        aria-label={accessibleLabel}
        data-slot={slot}
        className={cn(
          theme.slots.paginationButton,
          !enabled && theme.slots.paginationButtonDisabled,
          classNames?.button,
        )}
        onClick={() => requestPage(targetPage)}
      >
        {content}
      </button>
    );

    return (
      <div
        {...componentAttributes}
        {...paginationProps}
        ref={ref}
        role={role}
        aria-label={
          ariaLabel ?? (ariaLabelledBy === undefined ? navigationLabel : undefined)
        }
        aria-labelledby={ariaLabelledBy}
        data-monkeys-component="pagination"
        data-slot="root"
        data-mode={mode}
        data-controlled={isControlled || undefined}
        data-uncontrolled={isUncontrolled || undefined}
        className={cn(theme.slots.paginationRoot, classNames?.root, className)}
        style={{
          ...theme.styles.pagination,
          ...style,
        }}
      >
        <div
          aria-live="polite"
          data-slot="info"
          className={cn(theme.slots.paginationInfo, classNames?.info)}
        >
          {hasRenderableNode(label)
            ? label
            : total > 0
              ? messages.range({ start, end, total })
              : messages.emptyResults}
        </div>

        <div
          data-slot="controls"
          className={cn(theme.slots.paginationControls, classNames?.controls)}
        >
          {mode === 'pages'
            ? renderControl(
                'first',
                1,
                canPrevious,
                getControlContent(firstLabel, firstMessage),
                getControlAriaLabel(firstLabel, firstMessage),
              )
            : null}
          {renderControl(
            'previous',
            currentPage - 1,
            canPrevious,
            getControlContent(previousLabel, previousMessage),
            getControlAriaLabel(previousLabel, previousMessage),
          )}

          {mode === 'compact' ? (
            <span
              aria-current="page"
              aria-label={assertLocaleString(
                messages.currentPage({ page: currentPage, pageCount }),
                'currentPage',
              )}
              data-slot="page"
              data-page={currentPage}
              className={cn(theme.slots.paginationPage, classNames?.page)}
            >
              {currentPage} / {pageCount}
            </span>
          ) : (
            pageItems.map((item, index) => {
              if (item === 'ellipsis') {
                return (
                  <span
                    key={`ellipsis-${index}`}
                    aria-hidden="true"
                    data-slot="ellipsis"
                    className={cn(
                      theme.slots.paginationEllipsis,
                      classNames?.ellipsis,
                    )}
                  >
                    &hellip;
                  </span>
                );
              }

              const isCurrent = item === currentPage;
              return (
                <button
                  key={item}
                  type="button"
                  aria-current={isCurrent ? 'page' : undefined}
                  aria-label={assertLocaleString(
                    isCurrent
                      ? messages.currentPage({ page: item, pageCount })
                      : messages.page({ page: item, pageCount }),
                    isCurrent ? 'currentPage' : 'page',
                  )}
                  data-slot="page"
                  data-page={item}
                  data-current={isCurrent || undefined}
                  className={cn(
                    theme.slots.paginationButton,
                    isCurrent && theme.slots.paginationPage,
                    classNames?.button,
                    isCurrent && classNames?.page,
                  )}
                  onClick={() => requestPage(item)}
                >
                  {item}
                </button>
              );
            })
          )}

          {renderControl(
            'next',
            currentPage + 1,
            canNext,
            getControlContent(nextLabel, nextMessage),
            getControlAriaLabel(nextLabel, nextMessage),
          )}
          {mode === 'pages'
            ? renderControl(
                'last',
                pageCount,
                canNext,
                getControlContent(lastLabel, lastMessage),
                getControlAriaLabel(lastLabel, lastMessage),
              )
            : null}
        </div>
      </div>
    );
  },
);
